/**
 * ActionReceiptRenderer — renders an `action_receipt` artifact.
 *
 * Card-style summary of what the agent just *did* — verb icon, target label,
 * optional detail line, and a coloured status pill (success / partial /
 * failed). When the target carries an `external_url` the card surfaces an
 * "Open in {system}" affordance; failed receipts default to a `retry`
 * action so the user can re-run.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import type {
  ActionReceiptPayload,
  ActionResult,
  ActionTargetSystem,
  ActionVerb,
  Artifact,
  ArtifactAction,
} from "@/api/models"
import type { LucideIcon } from "@/components/icons"

import { Calendar, CheckCircle, Edit, ExternalLink, Folder, Mail, Ticket } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { ArtifactFrame } from "./ArtifactFrame"

const VERB_ICON: Record<ActionVerb, LucideIcon> = {
  sent_email: Mail,
  created_ticket: Ticket,
  updated_record: Edit,
  scheduled_meeting: Calendar,
  filed_document: Folder,
  other: CheckCircle,
}

const VERB_LABEL: Record<ActionVerb, string> = {
  sent_email: "Sent email",
  created_ticket: "Created ticket",
  updated_record: "Updated record",
  scheduled_meeting: "Scheduled meeting",
  filed_document: "Filed document",
  other: "Action completed",
}

const STATUS_COPY: Record<ActionResult, string> = {
  success: "Success",
  partial: "Partial",
  failed: "Failed",
}

const STATUS_VARIANT: Record<ActionResult, "success" | "warning" | "destructive"> = {
  success: "success",
  partial: "warning",
  failed: "destructive",
}

const SYSTEM_LABEL: Record<ActionTargetSystem, string> = {
  m365_outlook: "Outlook",
  m365_word: "Word",
  m365_excel: "Excel",
  m365_powerpoint: "PowerPoint",
  servicedesk: "ServiceDesk",
  netbox: "NetBox",
  sharepoint: "SharePoint",
  other: "external",
}

function buildDefaultActions(payload: ActionReceiptPayload): ArtifactAction[] {
  const actions: ArtifactAction[] = []
  if (payload.target.external_url) {
    actions.push({
      id: "open_external",
      label: `Open in ${SYSTEM_LABEL[payload.target.system]}`,
      icon: "external-link",
      primary: true,
    })
  }
  if (payload.result === "failed") {
    actions.push({ id: "retry", label: "Retry" })
  }
  return actions
}

export interface ActionReceiptRendererProps {
  artifact: Artifact & { payload: ActionReceiptPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

export function ActionReceiptRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: ActionReceiptRendererProps) {
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : buildDefaultActions(artifact.payload)
    return { ...artifact, actions }
  }, [artifact])

  const { payload } = artifact
  const VerbIcon = VERB_ICON[payload.verb]
  const statusVariant = STATUS_VARIANT[payload.result]
  const statusCopy = STATUS_COPY[payload.result]
  const systemLabel = SYSTEM_LABEL[payload.target.system]

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="flex items-start gap-3 py-1" data-testid="action-receipt-renderer-body">
        <div
          aria-hidden
          data-testid="action-receipt-verb-icon"
          data-verb={payload.verb}
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-md border",
            payload.result === "success" && STATUS_TONES.success.soft,
            payload.result === "partial" && "bg-warning/30 text-warning-ink",
            payload.result === "failed" && STATUS_TONES.destructive.soft
          )}
        >
          <VerbIcon className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p
              className="text-sm leading-snug font-medium"
              data-testid="action-receipt-target-label"
            >
              {payload.target.label}
            </p>
            <Badge
              data-status={payload.result}
              data-testid="action-receipt-status"
              variant={statusVariant}
            >
              {statusCopy}
            </Badge>
            {payload.rolled_back ? (
              <Badge data-testid="action-receipt-rolled-back" variant="secondary">
                Rolled back
              </Badge>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs" data-testid="action-receipt-verb-label">
            {VERB_LABEL[payload.verb]}
            {payload.target.external_id ? ` · ${payload.target.external_id}` : ""}
          </p>
          {payload.detail ? (
            <p className="text-sm" data-testid="action-receipt-detail">
              {payload.detail}
            </p>
          ) : null}
          {payload.target.external_url ? (
            <a
              className="text-primary inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
              data-testid="action-receipt-external-link"
              href={payload.target.external_url}
              rel="noreferrer noopener"
              target="_blank"
            >
              <ExternalLink aria-hidden className="size-3" />
              <span>Open in {systemLabel}</span>
            </a>
          ) : null}
        </div>
      </div>
    </ArtifactFrame>
  )
}
